import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import * as api from "../api/client";

const ACCEPT = ".pdf,.docx,.txt,.md";

function fmtDate(epochSeconds) {
  return epochSeconds
    ? new Date(epochSeconds * 1000).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
    : "—";
}

function fmtSize(bytes) {
  if (!bytes) return "—";
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** Shared modal shell, same markup as the other admin modals (RoleFormModal, UserFormModal). */
function Modal({ title, onClose, onSubmit, saving, submitLabel, error, children }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-modal" style={{ width: "min(520px, 100%)" }} onClick={(e) => e.stopPropagation()}>
        <div className="form-modal-head">
          <div className="form-modal-title">{title}</div>
          <button className="icon-btn" onClick={onClose} title="Close">
            ✕
          </button>
        </div>
        <Form onSubmit={onSubmit}>
          <div className="form-modal-body">
            {error && <Alert variant="danger">{error}</Alert>}
            {children}
          </div>
          <div className="form-modal-foot">
            <Button variant="outline-secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : submitLabel}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}

/** Create or edit a category / sub-category. */
function FolderModal({ mode, parent, folder, onClose, onDone }) {
  const [name, setName] = useState(folder?.name || "");
  const [description, setDescription] = useState(folder?.description || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isSub = mode === "edit" ? Boolean(folder.parentId) : Boolean(parent);

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (mode === "edit") await api.updateDocCategory(folder.id, { name: name.trim(), description: description.trim() });
      else await api.createDocCategory(name.trim(), description.trim(), parent?.id || null);
      onDone();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  const title =
    mode === "edit"
      ? `Edit ${isSub ? "sub-category" : "category"}`
      : parent
      ? `New sub-category in "${parent.name}"`
      : "New category";
  return (
    <Modal title={title} onClose={onClose} onSubmit={submit} saving={saving} error={error}
           submitLabel={mode === "edit" ? "Save changes" : "Create"}>
      <Form.Group className="mb-3">
        <Form.Label>Name</Form.Label>
        <Form.Control
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={isSub ? "e.g. HR Policy" : "e.g. HR"}
          maxLength={60}
          required
          autoFocus
        />
      </Form.Group>
      <Form.Group>
        <Form.Label>Description (optional)</Form.Label>
        <Form.Control value={description} onChange={(e) => setDescription(e.target.value)} />
      </Form.Group>
    </Modal>
  );
}

/** Upload a new document into one category or sub-category. */
function UploadModal({ folder, path, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    if (!file) return;
    setSaving(true);
    setError("");
    try {
      const doc = await api.uploadDocument(file, title.trim(), folder.id);
      onDone(`"${doc.title}" was added to ${path} (${plural(doc.chunkCount, "passage", "passages")} indexed).`);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <Modal title={`Add document to ${path}`} onClose={onClose} onSubmit={submit} saving={saving} error={error}
           submitLabel={saving ? "Reading and indexing…" : "Upload"}>
      <Form.Group className="mb-3">
        <Form.Label>File</Form.Label>
        <Form.Control
          type="file"
          accept={ACCEPT}
          onChange={(e) => {
            const f = e.target.files?.[0] || null;
            setFile(f);
            if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, ""));
          }}
          required
        />
        <Form.Text className="text-muted">PDF, Word (.docx), text or Markdown, up to 25 MB.</Form.Text>
      </Form.Group>
      <Form.Group>
        <Form.Label>Title</Form.Label>
        <Form.Control value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Leave Policy 2026" />
      </Form.Group>
      {saving && <div className="doc-sub" style={{ marginTop: 8 }}>Large files can take up to a minute.</div>}
    </Modal>
  );
}

/** Rename a document and/or move it to another category / sub-category. */
function EditDocumentModal({ doc, folderOptions, onClose, onDone }) {
  const [title, setTitle] = useState(doc.title);
  const [folderId, setFolderId] = useState(doc.folderId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.updateDocument(doc.id, { title: title.trim(), folderId });
      onDone();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <Modal title="Edit document" onClose={onClose} onSubmit={submit} saving={saving} error={error}
           submitLabel="Save changes">
      <Form.Group className="mb-3">
        <Form.Label>Title</Form.Label>
        <Form.Control value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus />
      </Form.Group>
      <Form.Group>
        <Form.Label>Category</Form.Label>
        <Form.Select value={folderId} onChange={(e) => setFolderId(e.target.value)}>
          {folderOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {o.path}
            </option>
          ))}
        </Form.Select>
        <Form.Text className="text-muted">Moving it changes which roles can be answered from it.</Form.Text>
      </Form.Group>
      <div className="doc-sub" style={{ marginTop: 12 }}>
        To upload a new version of the file, use ⟳ Replace file in the list.
      </div>
    </Modal>
  );
}

/** The documents directly inside one folder. */
function DocTable({ docs, onEdit, onReplace, onDelete, busyId }) {
  if (!docs.length) return <div className="doc-empty">No documents here yet.</div>;
  return (
    <table className="doc-table">
      <thead>
        <tr>
          <th>Document</th>
          <th>Passages</th>
          <th>Size</th>
          <th>Created</th>
          <th>Updated</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {docs.map((d) => (
          <tr key={d.id}>
            <td>
              <div className="doc-title">{d.title}</div>
              <div className="doc-sub">{d.filename}</div>
            </td>
            <td>{d.chunkCount}</td>
            <td>{fmtSize(d.sizeBytes)}</td>
            <td>
              <div>{fmtDate(d.createdAt)}</div>
              {d.uploadedBy && <div className="doc-sub">by {d.uploadedBy}</div>}
            </td>
            <td>
              <div>{fmtDate(d.updatedAt)}</div>
              {d.updatedBy && <div className="doc-sub">by {d.updatedBy}</div>}
            </td>
            <td className="doc-actions">
              {busyId === d.id ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <button className="dt-icon-btn" title="Replace file (upload a new version)" onClick={() => onReplace(d)}>
                    ⟳
                  </button>
                  <button className="dt-icon-btn" title="Rename or move" onClick={() => onEdit(d)}>
                    ✎
                  </button>
                  <button className="dt-icon-btn danger" title="Delete" onClick={() => onDelete(d)}>
                    🗑
                  </button>
                </>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** One category or sub-category: its heading row (counts, dates, actions) and, expanded, its contents. */
function FolderHead({ folder, isSub, open, onToggle, counts, onAddSub, onAddDoc, onEdit, onDelete }) {
  return (
    <div className={"doc-folder-head" + (isSub ? " sub" : "")}>
      <button className="doc-caret" onClick={onToggle} aria-label={open ? "Collapse" : "Expand"}>
        {open ? "▾" : "▸"}
      </button>
      <div className="doc-folder-name" onClick={onToggle}>
        <span className={isSub ? "doc-sub-icon" : "doc-cat-icon"}>{isSub ? "▤" : "▦"}</span>
        <span>{folder.name}</span>
        <span className="doc-sub">{counts}</span>
      </div>
      <div className="doc-folder-dates doc-sub">
        Created {fmtDate(folder.createdAt)} · Updated {fmtDate(folder.updatedAt)}
      </div>
      <div className="doc-folder-actions">
        {onAddSub && (
          <button className="doc-link" onClick={onAddSub}>
            + Sub-category
          </button>
        )}
        <button className="doc-link" onClick={onAddDoc}>
          + Add document
        </button>
        <button className="dt-icon-btn" title="Rename / describe" onClick={onEdit}>
          ✎
        </button>
        <button className="dt-icon-btn danger" title="Delete (must be empty)" onClick={onDelete}>
          🗑
        </button>
      </div>
      {folder.description && <div className="doc-folder-desc doc-sub">{folder.description}</div>}
    </div>
  );
}

// Admin-only. The knowledge base the Knowledge assistant answers from, organised as categories (HR,
// Training) holding sub-categories (HR › HR Policy) holding documents. Roles are granted a whole category
// or single sub-categories on the Roles page. An uploaded file is read, split into passages and indexed on
// the server before the upload returns, so a document listed here is already searchable.
export default function DocumentsPage() {
  const [folders, setFolders] = useState([]);
  const [docs, setDocs] = useState([]);
  const [store, setStore] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [query, setQuery] = useState("");
  const [closed, setClosed] = useState(() => new Set()); // collapsed folder ids (everything opens by default)
  const [modal, setModal] = useState(null); // { kind: "folder" | "upload" | "edit", ... }
  const [busyId, setBusyId] = useState(null);
  const replaceInput = useRef(null);
  const replaceTarget = useRef(null);

  const load = useCallback(async () => {
    try {
      const [f, d] = await Promise.all([api.listDocCategories(), api.listDocuments()]);
      setFolders(f.categories || []);
      setDocs(d.documents || []);
      setStore(d.vectorStore || "");
      setError("");
    } catch (e) {
      // A bare 404 means the backend predates this page (it was not restarted after the update).
      setError(
        e.message === "Not Found"
          ? "The server doesn't have the Documents feature yet. Restart the backend (after installing its " +
              "updated requirements) and reload this page."
          : e.message
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const categories = useMemo(() => folders.filter((f) => !f.parentId), [folders]);
  const subsOf = useMemo(() => {
    const m = {};
    for (const f of folders) if (f.parentId) (m[f.parentId] ||= []).push(f);
    return m;
  }, [folders]);
  const byId = useMemo(() => Object.fromEntries(folders.map((f) => [f.id, f])), [folders]);
  const pathOf = (f) => (f.parentId && byId[f.parentId] ? `${byId[f.parentId].name} › ${f.name}` : f.name);
  const folderOptions = categories.flatMap((c) => [
    { id: c.id, path: c.name },
    ...(subsOf[c.id] || []).map((s) => ({ id: s.id, path: `${c.name} › ${s.name}` })),
  ]);

  const q = query.trim().toLowerCase();
  const matches = (d) => !q || d.title.toLowerCase().includes(q) || d.filename.toLowerCase().includes(q);
  const docsIn = (id) => docs.filter((d) => d.folderId === id && matches(d));
  const totalIn = (c) => docs.filter((d) => d.folderId === c.id || (subsOf[c.id] || []).some((s) => s.id === d.folderId));
  const isOpen = (id) => Boolean(q) || !closed.has(id);
  const toggle = (id) =>
    setClosed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const done = async (message) => {
    setModal(null);
    if (message) setNotice(message);
    await load();
  };

  async function removeFolder(f) {
    if (!window.confirm(`Delete "${pathOf(f)}"? It must be empty. Roles that were granted it lose that access.`)) return;
    setError("");
    try {
      await api.deleteDocCategory(f.id);
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  async function removeDoc(d) {
    if (!window.confirm(`Delete "${d.title}"? The assistant will no longer answer from it.`)) return;
    setError("");
    try {
      await api.deleteDocument(d.id);
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  function startReplace(d) {
    replaceTarget.current = d;
    replaceInput.current.value = "";
    replaceInput.current.click();
  }

  async function replaceChosen(e) {
    const file = e.target.files?.[0];
    const d = replaceTarget.current;
    if (!file || !d) return;
    setBusyId(d.id);
    setError("");
    try {
      const updated = await api.replaceDocumentFile(d.id, file);
      setNotice(`"${updated.title}" now uses ${updated.filename} (${plural(updated.chunkCount, "passage", "passages")} indexed).`);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  }

  const docProps = {
    onEdit: (d) => setModal({ kind: "edit", doc: d }),
    onReplace: startReplace,
    onDelete: removeDoc,
    busyId,
  };

  return (
    <main className="main">
      <div className="topbar">
        <h1>Documents</h1>
        <p>
          HR policies, training material and procedures for the Knowledge assistant, organised by category. On the
          Roles page, grant a role a whole category or single sub-categories — a user is only answered from
          documents their role can read.
        </p>
      </div>
      <div className="page-body">
        {error && (
          <Alert variant="danger" onClose={() => setError("")} dismissible>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert variant="success" onClose={() => setNotice("")} dismissible>
            {notice}
          </Alert>
        )}
        <input ref={replaceInput} type="file" accept={ACCEPT} hidden onChange={replaceChosen} />

        <div className="doc-toolbar">
          <Button variant="success" onClick={() => setModal({ kind: "folder", mode: "create" })}>
            + New category
          </Button>
          <Form.Control
            className="doc-search"
            placeholder="Search documents…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <span className="doc-sub">
            {plural(categories.length, "category", "categories")} · {plural(docs.length, "document", "documents")}
            {store && ` · Vector store: ${store === "pgvector" ? "PostgreSQL + pgvector" : "PostgreSQL (pgvector not installed)"}`}
          </span>
        </div>

        {loading ? (
          <Spinner animation="border" />
        ) : categories.length === 0 ? (
          <div className="doc-panel doc-start">
            <div className="doc-panel-title">Start with a category</div>
            <div className="doc-sub">
              Create a category such as <strong>HR</strong>, add sub-categories inside it such as{" "}
              <strong>HR Policy</strong> and <strong>Payroll</strong>, then add documents to them.
            </div>
          </div>
        ) : (
          categories.map((c) => {
            const subs = subsOf[c.id] || [];
            const own = docsIn(c.id);
            const visibleSubs = subs.filter((s) => !q || docsIn(s.id).length);
            if (q && !own.length && !visibleSubs.length) return null;
            return (
              <section key={c.id} className="doc-panel doc-category">
                <FolderHead
                  folder={c}
                  open={isOpen(c.id)}
                  onToggle={() => toggle(c.id)}
                  counts={`${plural(subs.length, "sub-category", "sub-categories")} · ${plural(totalIn(c).length, "document", "documents")}`}
                  onAddSub={() => setModal({ kind: "folder", mode: "create", parent: c })}
                  onAddDoc={() => setModal({ kind: "upload", folder: c })}
                  onEdit={() => setModal({ kind: "folder", mode: "edit", folder: c })}
                  onDelete={() => removeFolder(c)}
                />
                {isOpen(c.id) && (
                  <div className="doc-category-body">
                    {(own.length > 0 || !subs.length) && <DocTable docs={own} {...docProps} />}
                    {visibleSubs.map((s) => (
                      <div key={s.id} className="doc-subcategory">
                        <FolderHead
                          folder={s}
                          isSub
                          open={isOpen(s.id)}
                          onToggle={() => toggle(s.id)}
                          counts={plural(docs.filter((d) => d.folderId === s.id).length, "document", "documents")}
                          onAddDoc={() => setModal({ kind: "upload", folder: s })}
                          onEdit={() => setModal({ kind: "folder", mode: "edit", folder: s })}
                          onDelete={() => removeFolder(s)}
                        />
                        {isOpen(s.id) && <DocTable docs={docsIn(s.id)} {...docProps} />}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            );
          })
        )}

        {modal?.kind === "folder" && (
          <FolderModal mode={modal.mode} parent={modal.parent} folder={modal.folder} onClose={() => setModal(null)}
                       onDone={() => done()} />
        )}
        {modal?.kind === "upload" && (
          <UploadModal folder={modal.folder} path={pathOf(modal.folder)} onClose={() => setModal(null)} onDone={done} />
        )}
        {modal?.kind === "edit" && (
          <EditDocumentModal doc={modal.doc} folderOptions={folderOptions} onClose={() => setModal(null)}
                             onDone={() => done()} />
        )}
      </div>
    </main>
  );
}
